/**
 * Every Staff page that something outside the Staff app links to.
 *
 * This is the source of truth for those routes: the Staff app declares them from here and builds
 * its links from here, so a page that moves is a change to this file rather than a string to find
 * in three repositories. Staff owns far more routes than these; one joins when a link to it has to
 * exist somewhere else.
 *
 * A route earns an entry once a caller can hold the ids it needs, whether a capability returned
 * them or a user supplied them. Notification schemes and scenarios are declared ahead of the
 * primary reads that will return them.
 */
export const STAFF_APP_ROUTES = {
    flowList: { label: 'Flow list', path: '/agents/flows' },
    flow: { label: 'Flow', path: '/agents/flows/:flowId' },
    flowDefinition: {
        label: 'Flow definition',
        path: '/agents/flows/:flowId/definitions/:flowDefinitionId',
    },
    // Retrieving a definition does not require its flow, so a link to one must not either.
    flowDefinitionWithoutFlow: {
        label: 'Flow definition, when its flow id is unknown',
        path: '/agents/flowDefinitions/:flowDefinitionId',
    },
    flowDefinitionBlock: {
        label: 'Flow definition block',
        path: '/agents/flows/:flowId/definitions/:flowDefinitionId',
        query: { selectedNodeId: 'blockId' },
    },
    flowRunList: { label: 'Flow run list', path: '/agents/flows/runs' },
    flowRun: { label: 'Flow run', path: '/agents/flowRuns/:flowRunId' },
    flowRunBlock: {
        label: 'Flow run block',
        path: '/agents/flows/:flowId/runs/:flowRunId',
        query: { selectedNodeId: 'blockId' },
    },
    flowTemplateLibrary: { label: 'Flow template library', path: '/agents/flows/create' },
    flowTemplate: { label: 'Flow template', path: '/agents/flows/create/:flowTemplateId' },
    triggerList: { label: 'Trigger list', path: '/triggers' },
    // kind decides which editor the page opens, and it guesses VoiceCall when the link omits it.
    trigger: {
        label: 'Trigger',
        path: '/triggers/:triggerId',
        query: { kind: 'triggerKind' },
    },
    signalList: { label: 'Signal list', path: '/signals' },
    signal: { label: 'Signal', path: '/signals/:signalId' },
    voiceLogList: { label: 'Voice log list', path: '/messaging/voice-calls' },
    // A voice call block reports the call it placed as notableVoiceCall.id, and the page that plays
    // it back is addressed with a /view suffix rather than by the id alone.
    voiceLog: { label: 'Voice log', path: '/messaging/voice-calls/:voiceCallId/view' },
    voiceAssistantDefinition: {
        label: 'Voice assistant configuration',
        path: '/assistants/definitions/:assistantDefinitionId',
    },
    scenarioList: { label: 'Scenario list', path: '/fingerprint/scenarios' },
    scenario: { label: 'Scenario', path: '/fingerprint/scenarios/:scenarioId' },
    // Where a previsit form set's progress is rendered, the set itself having no page, and where a
    // flow run whose entityType is encounter is about.
    appointment: {
        label: 'Appointment, with its previsit form progress',
        path: '/encounters/:encounterId',
    },
    // The path says patients, the id is the EHR information record's, which is what a previsit form
    // set and a flow run whose entityType is ehrInformation both report.
    patientRecord: {
        label: 'Patient record, addressed by its EHR information id',
        path: '/entity/patients/:ehrInformationId',
    },
    previsitFormTemplateList: { label: 'Previsit form list', path: '/previsit-form-templates' },
    // Staff calls these forms, and the page opens on the questions, which is what a reader asking
    // about a form wants. A previsit form set, being one patient's copy, has no page of its own:
    // its progress is on the appointment above.
    previsitFormTemplate: {
        label: 'Previsit form, opening on its questions',
        path: '/previsit-form-templates/:previsitFormTemplateId',
    },
    // One page per channel, so the kind a content reports selects the route rather than a parameter.
    notificationContentSms: {
        label: 'Notification content, kind SMS',
        path: '/notifications/sms/:contentId',
    },
    notificationContentEmail: {
        label: 'Notification content, kind EMAIL',
        path: '/notifications/email/:contentId',
    },
    notificationContentVoice: {
        label: 'Notification content, kind VOICE',
        path: '/notifications/voice/:contentId',
    },
    notificationSchemeList: { label: 'Notification scheme list', path: '/notifications/scheme' },
    notificationScheme: { label: 'Notification scheme', path: '/notifications/scheme/:schemeId' },
    notificationSchedule: {
        label: 'Notification schedule',
        path: '/notifications/schedule/:scheduleId',
    },
};
//# sourceMappingURL=routes.js.map